import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Search, PenTool, Code, FlaskConical, Rocket } from 'lucide-react';

const steps = [
  {
    icon: Search,
    title: 'Discovery',
    description: 'Together we map your business goals, technical requirements, and competitive landscape so the solution is scoped precisely to your needs — no bloat, no guesswork.',
  },
  {
    icon: PenTool,
    title: 'Architect',
    description: 'I design the system blueprint, data flow, and user experience before a single line of code is written. This is the human-driven strategic work that defines your product.',
  },
  {
    icon: Code,
    title: 'Build & Accelerate',
    description: 'With the architecture locked in, my AI-accelerated workflow handles routine scaffolding while I focus entirely on your custom business logic, integrations, and security — cutting development time without cutting corners.',
  },
  {
    icon: FlaskConical,
    title: 'Test together',
    description: "I send you a testing version before everything is polished. Some rough edges are intentional — known bugs we'll fix — and your real-world use helps us find the unknown ones, plus catches direction changes early. Saves us both from spending hours fixing things you might want different anyway.",
  },
  {
    icon: Rocket,
    title: 'Launch',
    description: "I deploy to production with real testing, performance validation, and a security review. And I don't disappear after launch — ongoing support and troubleshooting keep your systems running smoothly.",
  },
];

const Process = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.section
      ref={ref}
      id="process"
      className="py-24 bg-gray-50"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 50 }}
      transition={{ duration: 0.8 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="brand-header font-sans text-4xl font-extrabold sm:text-5xl mb-4">
            How I Build
          </h2>
          <p className="text-lg text.body max-w-2xl mx-auto">
            A transparent process built for speed, quality, and zero surprises — with me, not a ticket queue.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: inView ? 1 : 0, y: inView ? 0 : 20 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <div className="relative inline-flex items-center justify-center mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center">
                  <step.icon className="w-7 h-7 text-purple-600" />
                </div>
                <span className="absolute -top-2 -right-2 w-7 h-7 bg-purple-600 text-white text-sm font-bold rounded-full flex items-center justify-center">
                  {index + 1}
                </span>
              </div>
              <h3 className="text-xl font-bold text.heading mb-2">{step.title}</h3>
              <p className="text.body text-sm leading-relaxed">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default Process;
